import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MasterAdminMaintenancePreview from '../components/MasterAdminMaintenancePreview';
import MasterAdminShell, { type MasterAdminTab } from '../components/MasterAdminShell';
import LearningModuleEditor from '../components/LearningModuleEditor';
import { useAuth } from '../context/AuthContext';
import { masterAdminApi } from '../services/api';
import { isMasterAdminDirectAccessAllowed } from '../lib/masterAdminAccess';
import { getMasterTestingKey } from '../lib/masterTestingKey';
import { masterAdminStudentAssessmentPath } from '../lib/classroomRoutes';
import './OrgAdmin.css';
import './SystemAdmin.css';

export { MASTER_ADMIN_CONSOLE_TITLE } from '../components/MasterAdminShell';

type KnowledgeHubTab = 'masterSpecifications' | 'vaultPartitionMap';

type SubscriptionState = 'Active' | 'Trialing' | 'Lapsed';

interface ProgramStudentRow {
  studentKey: string;
  keyType: string;
  keyTypeLabel: string;
  dateEnrolled: string;
  studentName: string;
  phone: string;
  email: string;
  locationAddress: string;
  accountStatus: string;
  certificationCompetent?: boolean;
  archiveReason?: string | null;
}

function normalizeStudentAccountStatus(status: string): 'Active' | 'Not Active' | 'Assessed' {
  const value = status?.trim() ?? '';
  if (value === 'Assessed' || value === 'Completed') return 'Assessed';
  if (value === 'Not Active' || value === 'Inactive' || value === 'Lapsed') return 'Not Active';
  if (value === 'Active') return 'Active';
  return 'Active';
}

function studentStatusTone(status: string): 'active' | 'not-active' | 'assessed' {
  const normalized = normalizeStudentAccountStatus(status);
  if (normalized === 'Assessed') return 'assessed';
  if (normalized === 'Not Active') return 'not-active';
  return 'active';
}

type OrgSubscriptionState = 'Package Active' | 'Provisioned' | 'Lapsed';

interface LearningModuleSummaryRow {
  moduleId: string;
  title: string;
  stage: string;
  updatedAt: string | null;
}

interface OrganisationRow {
  organisationKey: string;
  dateEnrolled: string;
  organisationName: string;
  contactName: string;
  phone: string;
  email: string;
  subscriptionState: OrgSubscriptionState;
  timeLeft: string;
}

import {
  KNOWLEDGE_ENGINE_ANCHORS,
  LEARNING_MODULES,
  STRUCTURAL_CORE_LIBRARY_MATRIX,
  VAULT_PARTITIONS,
} from '../lib/programKnowledgeRegistry';
import {
  createProgramKeyEmailDraft,
  openGmailCompose,
  type ProgramKeyEmailDraft,
} from '../lib/programKeyEmail';

type SupportStatus = 'Open — Awaiting Review' | 'In Progress' | 'Resolved';

interface PrivateIncidentRow {
  id: string;
  menteeKey: string;
  timestamp: string;
  alertSource: string;
  incidentStatus: string;
  systemResponse: string;
  resolved: boolean;
  telemetry: string;
}

interface CorporateSupportRow {
  organisationKey: string;
  organisationName: string;
  contactName: string;
  status: SupportStatus;
  supportDocument: string;
}

const SUPPORT_STATUS_OPTIONS: SupportStatus[] = [
  'Open — Awaiting Review',
  'In Progress',
  'Resolved',
];

type KeyGeneratorOption = 'individual_student' | 'org_student' | 'org_admin';

type ProgramKeyStatus = 'Active' | 'Revoked';

interface ProgramKeyLedgerRow {
  id: string;
  programKey: string;
  typeRole: string;
  status: ProgramKeyStatus;
  registeredEmail: string;
  registeredName: string;
  targetRoute: string;
}

const DELETE_CONFIRM_MESSAGE = 'Are you sure you want to permanently delete this entry?';

function mapProgramKeyRow(row: {
  id: number;
  programKey: string;
  typeRole: string;
  status: string;
  registeredEmail?: string | null;
  registeredName?: string | null;
  targetRoute?: string | null;
}): ProgramKeyLedgerRow {
  return {
    id: String(row.id),
    programKey: row.programKey,
    typeRole: row.typeRole,
    status: row.status === 'Revoked' ? 'Revoked' : 'Active',
    registeredEmail: row.registeredEmail || '—',
    registeredName: row.registeredName || '—',
    targetRoute: row.targetRoute || '—',
  };
}

export default function SystemAdmin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, login } = useAuth();
  const masterTestBootstrapRef = useRef(false);
  const [activeSection, setActiveSection] = useState<MasterAdminTab>('Learning Program Knowledge Hub');
  const [knowledgeHubTab, setKnowledgeHubTab] = useState<KnowledgeHubTab>('masterSpecifications');
  const [programStudentRows, setProgramStudentRows] = useState<ProgramStudentRow[]>([]);
  const [archivedStudentRows, setArchivedStudentRows] = useState<ProgramStudentRow[]>([]);
  const [organisationRows, setOrganisationRows] = useState<OrganisationRow[]>([]);
  const [registryLoading, setRegistryLoading] = useState(true);
  useEffect(() => {
    if (authLoading || user || masterTestBootstrapRef.current) return;
    if (!isMasterAdminDirectAccessAllowed()) return;

    const masterKey = getMasterTestingKey();
    if (!masterKey) return;

    masterTestBootstrapRef.current = true;
    void login(masterKey).catch(() => {
      masterTestBootstrapRef.current = false;
    });
  }, [authLoading, user, login]);

  const [keyGeneratorOption, setKeyGeneratorOption] = useState<KeyGeneratorOption>('individual_student');
  const [individualKeyForm, setIndividualKeyForm] = useState({
    clientName: '',
    clientEmail: '',
  });
  const [orgStudentKeyForm, setOrgStudentKeyForm] = useState({
    studentName: '',
    studentEmail: '',
  });
  const [orgAdminKeyForm, setOrgAdminKeyForm] = useState({
    adminName: '',
    organisationName: '',
    adminEmail: '',
  });
  const [programKeyLedgerRows, setProgramKeyLedgerRows] = useState<ProgramKeyLedgerRow[]>([]);
  const [programKeysLoading, setProgramKeysLoading] = useState(false);
  const [programKeyEmailDraft, setProgramKeyEmailDraft] = useState<ProgramKeyEmailDraft | null>(null);
  const [programKeyEmailNotice, setProgramKeyEmailNotice] = useState('');
  const [supportDocumentModal, setSupportDocumentModal] = useState<string | null>(null);
  const [privateIncidents, setPrivateIncidents] = useState<PrivateIncidentRow[]>([]);
  const [corporateSupportRows, setCorporateSupportRows] = useState<CorporateSupportRow[]>([]);
  const [incidentReviewId, setIncidentReviewId] = useState<string | null>(null);
  const [triageOrgKey, setTriageOrgKey] = useState<string | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [learningModuleRows, setLearningModuleRows] = useState<LearningModuleSummaryRow[]>([]);
  const [learningModulesLoading, setLearningModulesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      masterAdminApi.listProgramStudents(),
      masterAdminApi.listOrganisations(),
    ])
      .then(([studentData, orgData]) => {
        if (cancelled) return;
        setProgramStudentRows(studentData.activeRows || studentData.rows || []);
        setArchivedStudentRows(studentData.archivedRows || []);
        setOrganisationRows(orgData.rows || []);
      })
      .catch(() => {
        if (cancelled) return;
        setProgramStudentRows([]);
        setOrganisationRows([]);
      })
      .finally(() => {
        if (!cancelled) setRegistryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (activeSection !== 'Key Generator') return;

    let cancelled = false;
    setProgramKeysLoading(true);

    masterAdminApi.listProgramKeys()
      .then((data) => {
        if (cancelled) return;
        setProgramKeyLedgerRows((data.rows || []).map(mapProgramKeyRow));
      })
      .catch(() => {
        if (cancelled) return;
        setProgramKeyLedgerRows([]);
      })
      .finally(() => {
        if (!cancelled) setProgramKeysLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'Learning Modules') return;

    let cancelled = false;
    setLearningModulesLoading(true);

    masterAdminApi.listLearningModules()
      .then((data) => {
        if (cancelled) return;
        setLearningModuleRows(data.rows || LEARNING_MODULES.map((row) => ({
          moduleId: row.moduleId,
          title: row.title,
          stage: row.stage,
          updatedAt: null,
        })));
      })
      .catch(() => {
        if (cancelled) return;
        setLearningModuleRows(LEARNING_MODULES.map((row) => ({
          moduleId: row.moduleId,
          title: row.title,
          stage: row.stage,
          updatedAt: null,
        })));
      })
      .finally(() => {
        if (!cancelled) setLearningModulesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'Learning Modules') {
      setSelectedModuleId(null);
    }
  }, [activeSection]);

  const reloadStudentRegistry = () => {
    masterAdminApi.listProgramStudents()
      .then((studentData) => {
        setProgramStudentRows(studentData.activeRows || studentData.rows || []);
        setArchivedStudentRows(studentData.archivedRows || []);
      })
      .catch(() => {
        setProgramStudentRows([]);
        setArchivedStudentRows([]);
      });
  };

  const handleArchiveStudent = async (studentKey: string) => {
    try {
      const result = await masterAdminApi.archiveProgramStudent(studentKey);
      reloadStudentRegistry();
      const archivePath = result?.archiveSpreadsheet?.filePath;
      if (archivePath) {
        window.alert(`Student archived.\n\nRecord saved to:\n${archivePath}`);
      }
    } catch (err: unknown) {
      const reason = typeof err === 'object' && err && 'error' in err
        ? String((err as { error?: string }).error || '')
        : '';
      if (reason === 'already_certified') {
        window.alert('This student is already certified. Open Assessment from the Key Archive section below.');
      } else {
        window.alert('Unable to archive this student.');
      }
    }
  };

  const reloadOrganisationRegistry = () => {
    masterAdminApi.listOrganisations()
      .then((orgData) => {
        setOrganisationRows(orgData.rows || []);
      })
      .catch(() => {
        setOrganisationRows([]);
      });
  };

  useEffect(() => {
    if (activeSection !== 'Organisations') return;

    let cancelled = false;
    setRegistryLoading(true);

    masterAdminApi.listOrganisations()
      .then((orgData) => {
        if (cancelled) return;
        setOrganisationRows(orgData.rows || []);
      })
      .catch(() => {
        if (cancelled) return;
        setOrganisationRows([]);
      })
      .finally(() => {
        if (!cancelled) setRegistryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeSection]);

  const handleArchiveOrganisation = async (organisationKey: string) => {
    if (!window.confirm(
      `Archive ${organisationKey}? The organisation record will be written to the vault archive spreadsheet.`,
    )) return;

    try {
      const result = await masterAdminApi.archiveOrganisation(organisationKey);
      reloadOrganisationRegistry();
      const archivePath = result?.archiveSpreadsheet?.filePath;
      if (archivePath) {
        window.alert(`Organisation archived.\n\nRecord saved to:\n${archivePath}`);
      }
    } catch {
      window.alert('Unable to archive this organisation.');
    }
  };

  const updateIndividualKeyForm = (field: keyof typeof individualKeyForm, value: string) => {
    setIndividualKeyForm((current) => ({ ...current, [field]: value }));
  };

  const updateOrgStudentKeyForm = (field: keyof typeof orgStudentKeyForm, value: string) => {
    setOrgStudentKeyForm((current) => ({ ...current, [field]: value }));
  };

  const updateOrgAdminKeyForm = (field: keyof typeof orgAdminKeyForm, value: string) => {
    setOrgAdminKeyForm((current) => ({ ...current, [field]: value }));
  };

  const generateProgramKey = async () => {
    let allocationType: 'INDIVIDUAL_STUDENT' | 'ORG_STUDENT' | 'ORG_ADMIN';
    let registeredName = '';
    let registeredEmail = '';
    let organisationName: string | undefined;

    if (keyGeneratorOption === 'individual_student') {
      allocationType = 'INDIVIDUAL_STUDENT';
      registeredName = individualKeyForm.clientName.trim();
      registeredEmail = individualKeyForm.clientEmail.trim();
    } else if (keyGeneratorOption === 'org_student') {
      allocationType = 'ORG_STUDENT';
      registeredName = orgStudentKeyForm.studentName.trim();
      registeredEmail = orgStudentKeyForm.studentEmail.trim();
    } else {
      allocationType = 'ORG_ADMIN';
      registeredName = orgAdminKeyForm.adminName.trim();
      registeredEmail = orgAdminKeyForm.adminEmail.trim();
      organisationName = orgAdminKeyForm.organisationName.trim();
    }

    if (!registeredName || !registeredEmail) {
      window.alert('Registered name and email are required.');
      return;
    }

    if (allocationType === 'ORG_ADMIN' && !organisationName) {
      window.alert('Organisation name is required for administrator keys.');
      return;
    }

    try {
      const { row } = await masterAdminApi.createProgramKey({
        allocationType,
        registeredEmail,
        registeredName,
        organisationName,
      });
      setProgramKeyLedgerRows((rows) => [mapProgramKeyRow(row), ...rows]);
      setProgramKeyEmailDraft(createProgramKeyEmailDraft(registeredName, registeredEmail, row.programKey));
      setProgramKeyEmailNotice('');
    } catch {
      window.alert('Unable to generate program key.');
    }
  };

  const openProgramKeyEmailDraft = (row: ProgramKeyLedgerRow) => {
    if (!row.registeredEmail || row.registeredEmail === '—') {
      window.alert('This ledger entry has no registered email.');
      return;
    }

    setProgramKeyEmailDraft(createProgramKeyEmailDraft(
      row.registeredName === '—' ? '' : row.registeredName,
      row.registeredEmail,
      row.programKey,
    ));
    setProgramKeyEmailNotice('');
  };

  const handleOpenProgramKeyGmail = () => {
    if (!programKeyEmailDraft) return;

    if (!programKeyEmailDraft.to.trim()) {
      window.alert('Recipient email is required.');
      return;
    }

    openGmailCompose(programKeyEmailDraft);
    setProgramKeyEmailNotice('Gmail compose opened in a new tab. Review the message and click Send when ready.');
  };

  const deleteProgramKeyRow = async (rowId: string, programKey: string) => {
    if (!window.confirm(DELETE_CONFIRM_MESSAGE)) return;

    try {
      await masterAdminApi.deleteProgramKey(programKey);
      setProgramKeyLedgerRows((rows) => rows.filter((row) => row.id !== rowId));
    } catch {
      window.alert('Unable to delete this entry.');
    }
  };

  const activeIncidentReview = privateIncidents.find((row) => row.id === incidentReviewId) ?? null;

  const resolveIncident = (incidentId: string) => {
    setPrivateIncidents((rows) =>
      rows.map((row) =>
        row.id === incidentId
          ? { ...row, resolved: true, incidentStatus: 'Resolved' }
          : row,
      ),
    );
    setIncidentReviewId(null);
  };

  const setCorporateSupportStatus = (organisationKey: string, status: SupportStatus) => {
    setCorporateSupportRows((rows) =>
      rows.map((row) => (row.organisationKey === organisationKey ? { ...row, status } : row)),
    );
    setTriageOrgKey(null);
  };

  const renderSystemData = () => (
    <div className="org-panel-card system-data-workspace">
      <h2 className="section-header system-data-heading">System Data</h2>
      <p className="system-data-intro">
        Aggregates a combined logging data spreadsheet displaying global software runtime warnings, platform
        exceptions, and client institutional error reports alongside the real-time Support_Tickets table array.
      </p>

      <div className="system-data-stack">
        <div className="system-data-topic">
          <h3 className="system-data-topic-heading">
            Private Student Incidents (Red Phone Protocols & System Errors)
          </h3>
          <div className="system-data-table-wrap">
            <table className="system-data-table">
              <thead>
                <tr>
                  <th>Student Key</th>
                  <th>Timestamp</th>
                  <th>Alert Source</th>
                  <th>Incident Status</th>
                  <th>System Response</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {privateIncidents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="system-data-empty-row">
                      No private student incidents logged.
                    </td>
                  </tr>
                ) : (
                  privateIncidents.map((row) => (
                  <tr key={row.id}>
                    <td><span className="system-data-key">{row.menteeKey}</span></td>
                    <td>{row.timestamp}</td>
                    <td>{row.alertSource}</td>
                    <td>
                      <span
                        className={`system-data-incident-badge ${
                          row.resolved
                            ? 'system-data-incident-resolved'
                            : row.alertSource.includes('Red Phone')
                              ? 'system-data-incident-critical'
                              : 'system-data-incident-high'
                        }`}
                      >
                        {row.incidentStatus}
                      </span>
                    </td>
                    <td>{row.systemResponse}</td>
                    <td>
                      <button
                        type="button"
                        className="system-data-action-link"
                        onClick={() => setIncidentReviewId(row.id)}
                      >
                        [ Review Incident ]
                      </button>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="system-data-topic">
          <h3 className="system-data-topic-heading">
            Corporate Support Submissions (Organisations)
          </h3>
          <div className="system-data-table-wrap">
            <table className="system-data-table system-data-table-corporate">
              <thead>
                <tr>
                  <th>Organisation Key</th>
                  <th>Organisation Name</th>
                  <th>Contact Name</th>
                  <th>Support File Link</th>
                  <th>Status</th>
                  <th>Admin Control</th>
                </tr>
              </thead>
              <tbody>
                {corporateSupportRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="system-data-empty-row">
                      No corporate support submissions logged.
                    </td>
                  </tr>
                ) : (
                  corporateSupportRows.map((row) => (
                  <tr key={row.organisationKey}>
                    <td><span className="system-data-key">{row.organisationKey}</span></td>
                    <td>{row.organisationName}</td>
                    <td>{row.contactName}</td>
                    <td>
                      <button
                        type="button"
                        className="system-data-doc-link"
                        onClick={() => setSupportDocumentModal(row.supportDocument)}
                      >
                        [ View Support Document ]
                      </button>
                    </td>
                    <td>
                      <span
                        className={`system-data-support-status ${
                          row.status === 'Resolved'
                            ? 'system-data-support-resolved'
                            : row.status === 'In Progress'
                              ? 'system-data-support-progress'
                              : 'system-data-support-open'
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="system-data-action-link"
                        onClick={() => setTriageOrgKey(row.organisationKey)}
                      >
                        [ Triage Status ]
                      </button>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {activeIncidentReview && (
        <div
          className="system-data-modal-overlay"
          role="presentation"
          onClick={() => setIncidentReviewId(null)}
        >
          <div
            className="system-data-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="system-data-incident-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="system-data-incident-modal-title" className="system-data-modal-title">
              Incident Telemetry — {activeIncidentReview.menteeKey}
            </h4>
            <pre className="system-data-modal-body">{activeIncidentReview.telemetry}</pre>
            {!activeIncidentReview.resolved && (
              <button
                type="button"
                className="btn-primary system-data-modal-action"
                onClick={() => resolveIncident(activeIncidentReview.id)}
              >
                [ Resolve & Clear Incident ]
              </button>
            )}
            <button type="button" className="btn-primary system-data-modal-close" onClick={() => setIncidentReviewId(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {triageOrgKey && (
        <div
          className="system-data-modal-overlay"
          role="presentation"
          onClick={() => setTriageOrgKey(null)}
        >
          <div
            className="system-data-modal system-data-triage-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="system-data-triage-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="system-data-triage-title" className="system-data-modal-title">Triage Status</h4>
            <p className="system-data-triage-subtext">
              {corporateSupportRows.find((row) => row.organisationKey === triageOrgKey)?.organisationName}
            </p>
            <div className="system-data-triage-options">
              {SUPPORT_STATUS_OPTIONS.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`system-data-triage-option ${
                    corporateSupportRows.find((row) => row.organisationKey === triageOrgKey)?.status === status
                      ? 'system-data-triage-option-active'
                      : ''
                  }`}
                  onClick={() => setCorporateSupportStatus(triageOrgKey, status)}
                >
                  {status}
                </button>
              ))}
            </div>
            <button type="button" className="btn-primary system-data-modal-close" onClick={() => setTriageOrgKey(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {supportDocumentModal && (
        <div
          className="system-data-modal-overlay"
          role="presentation"
          onClick={() => setSupportDocumentModal(null)}
        >
          <div
            className="system-data-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="system-data-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="system-data-modal-title" className="system-data-modal-title">Support Document</h4>
            <pre className="system-data-modal-body">{supportDocumentModal}</pre>
            <button type="button" className="btn-primary system-data-modal-close" onClick={() => setSupportDocumentModal(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderProgramKeyEmailModal = () => {
    if (!programKeyEmailDraft) return null;

    return (
      <div
        className="system-data-modal-overlay"
        role="presentation"
        onClick={() => setProgramKeyEmailDraft(null)}
      >
        <div
          className="system-data-modal program-key-email-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="program-key-email-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h4 id="program-key-email-modal-title" className="system-data-modal-title">
            Send Classroom Access Key
          </h4>
          <p className="program-key-email-modal-intro">
            Review the recipient details and message below. When ready, open Gmail to send the key to your client.
          </p>
          <label className="program-key-email-field">
            <span className="program-key-email-field-label">To</span>
            <input
              type="email"
              className="input-field program-key-email-input"
              value={programKeyEmailDraft.to}
              onChange={(e) => {
                setProgramKeyEmailDraft((current) => (
                  current ? { ...current, to: e.target.value } : current
                ));
              }}
            />
          </label>
          <label className="program-key-email-field">
            <span className="program-key-email-field-label">Subject</span>
            <input
              type="text"
              className="input-field program-key-email-input"
              value={programKeyEmailDraft.subject}
              onChange={(e) => {
                setProgramKeyEmailDraft((current) => (
                  current ? { ...current, subject: e.target.value } : current
                ));
              }}
            />
          </label>
          <label className="program-key-email-field">
            <span className="program-key-email-field-label">Message</span>
            <textarea
              className="program-key-email-textarea"
              rows={18}
              value={programKeyEmailDraft.body}
              onChange={(e) => {
                setProgramKeyEmailDraft((current) => (
                  current ? { ...current, body: e.target.value } : current
                ));
              }}
            />
          </label>
          {programKeyEmailNotice ? (
            <p className="program-key-email-notice" role="status">{programKeyEmailNotice}</p>
          ) : null}
          <div className="program-key-email-modal-actions">
            <button
              type="button"
              className="btn-primary program-key-email-gmail-btn"
              onClick={handleOpenProgramKeyGmail}
            >
              Open in Gmail
            </button>
            <button
              type="button"
              className="btn-primary system-data-modal-close"
              onClick={() => setProgramKeyEmailDraft(null)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderKeyGenerator = () => (
    <div className="org-panel-card demo-key-workspace">
      <h2 className="section-header demo-key-heading">Key Generator</h2>
      <p className="demo-key-intro">
        Primary allocation console for pre-authorized Mentology Classroom program keys.
        Mint keys, register recipients, and route each allocation through its onboarding pipeline.
      </p>

      <div className="demo-key-creation-panel">
        <form className="demo-key-form" onSubmit={(e) => e.preventDefault()}>
          <div className="demo-key-entry-field demo-key-generator-format">
            <p className="demo-key-entry-label">Program Key Allocation</p>
            <p className="demo-key-entry-format">
              Option A: MPL-#### · Option B: MPLO-#### · Option C: MPL-XXX-####
            </p>
          </div>
          <div className="demo-key-columns demo-key-columns-three" role="group" aria-label="Program key type">
            <div
              className={`demo-key-column ${keyGeneratorOption === 'individual_student' ? 'demo-key-column-active' : ''}`}
            >
              <button
                type="button"
                aria-pressed={keyGeneratorOption === 'individual_student'}
                className={`demo-key-column-heading ${keyGeneratorOption === 'individual_student' ? 'demo-key-column-heading-active' : ''}`}
                onClick={() => setKeyGeneratorOption('individual_student')}
              >
                Option A — Individual Student Key
              </button>
              <p className="demo-key-option-pipeline">Format: MPL-#### · Route: /student-setup → /learning-room-elt</p>
              <div className="demo-key-field-stack">
                <input
                  className="input-field demo-key-input"
                  type="text"
                  placeholder="Student Name"
                  value={individualKeyForm.clientName}
                  onChange={(e) => updateIndividualKeyForm('clientName', e.target.value)}
                />
                <input
                  className="input-field demo-key-input"
                  type="email"
                  placeholder="Registered Email"
                  value={individualKeyForm.clientEmail}
                  onChange={(e) => updateIndividualKeyForm('clientEmail', e.target.value)}
                />
              </div>
            </div>

            <div
              className={`demo-key-column ${keyGeneratorOption === 'org_student' ? 'demo-key-column-active' : ''}`}
            >
              <button
                type="button"
                aria-pressed={keyGeneratorOption === 'org_student'}
                className={`demo-key-column-heading ${keyGeneratorOption === 'org_student' ? 'demo-key-column-heading-active' : ''}`}
                onClick={() => setKeyGeneratorOption('org_student')}
              >
                Option B — Organisation Student Key
              </button>
              <p className="demo-key-option-pipeline">Format: MPLO-#### · Route: /learning-room-elt</p>
              <div className="demo-key-field-stack">
                <input
                  className="input-field demo-key-input"
                  type="text"
                  placeholder="Student Name"
                  value={orgStudentKeyForm.studentName}
                  onChange={(e) => updateOrgStudentKeyForm('studentName', e.target.value)}
                />
                <input
                  className="input-field demo-key-input"
                  type="email"
                  placeholder="Registered Email"
                  value={orgStudentKeyForm.studentEmail}
                  onChange={(e) => updateOrgStudentKeyForm('studentEmail', e.target.value)}
                />
              </div>
            </div>

            <div
              className={`demo-key-column ${keyGeneratorOption === 'org_admin' ? 'demo-key-column-active' : ''}`}
            >
              <button
                type="button"
                aria-pressed={keyGeneratorOption === 'org_admin'}
                className={`demo-key-column-heading ${keyGeneratorOption === 'org_admin' ? 'demo-key-column-heading-active' : ''}`}
                onClick={() => setKeyGeneratorOption('org_admin')}
              >
                Option C — Organisation Administrator Key
              </button>
              <p className="demo-key-option-pipeline">Format: MPL-[ORG]-#### · Route: /system-admin</p>
              <div className="demo-key-field-stack">
                <input
                  className="input-field demo-key-input"
                  type="text"
                  placeholder="Administrator Name"
                  value={orgAdminKeyForm.adminName}
                  onChange={(e) => updateOrgAdminKeyForm('adminName', e.target.value)}
                />
                <input
                  className="input-field demo-key-input"
                  type="text"
                  placeholder="Organisation Name (3-letter org code derived)"
                  value={orgAdminKeyForm.organisationName}
                  onChange={(e) => updateOrgAdminKeyForm('organisationName', e.target.value)}
                />
                <input
                  className="input-field demo-key-input"
                  type="email"
                  placeholder="Registered Email"
                  value={orgAdminKeyForm.adminEmail}
                  onChange={(e) => updateOrgAdminKeyForm('adminEmail', e.target.value)}
                />
              </div>
            </div>
          </div>

          <button type="button" className="btn-primary demo-key-action-btn" onClick={generateProgramKey}>
            [ + GENERATE KEY ]
          </button>
        </form>
      </div>

      <div className="demo-key-ledger-section">
        <h3 className="demo-key-ledger-heading">Control Line Ledger</h3>
        <div className="demo-key-ledger-wrap">
          <table className="demo-key-ledger-table">
            <thead>
              <tr>
                <th>KEY</th>
                <th>TYPE / ROLE</th>
                <th>STATUS</th>
                <th>REGISTERED EMAIL</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {programKeysLoading ? (
                <tr>
                  <td colSpan={5} className="demo-key-ledger-empty">
                    Loading control line ledger…
                  </td>
                </tr>
              ) : programKeyLedgerRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="demo-key-ledger-empty">
                    No program keys allocated yet.
                  </td>
                </tr>
              ) : (
                programKeyLedgerRows.map((row) => (
                  <tr key={row.id}>
                    <td><span className="demo-key-ledger-code">{row.programKey}</span></td>
                    <td>{row.typeRole}</td>
                    <td>
                      <span className={`demo-key-ledger-status demo-key-ledger-status-${row.status === 'Active' ? 'active' : 'unused'}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>{row.registeredEmail}</td>
                    <td>
                      <div className="demo-key-ledger-actions">
                        <button
                          type="button"
                          className="demo-key-email-btn"
                          onClick={() => openProgramKeyEmailDraft(row)}
                        >
                          EMAIL
                        </button>
                        <button
                          type="button"
                          className="demo-key-delete-btn"
                          onClick={() => deleteProgramKeyRow(row.id, row.programKey)}
                        >
                          DELETE
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const openStudentAssessment = (studentKey: string) => {
    navigate(masterAdminStudentAssessmentPath(studentKey));
  };

  const renderActiveStudentRow = (row: ProgramStudentRow) => {
    const accountStatus = normalizeStudentAccountStatus(row.accountStatus);
    const statusTone = studentStatusTone(accountStatus);
    return (
      <tr key={row.studentKey}>
        <td>
          <button
            type="button"
            className="window-a-key window-a-key-link"
            onClick={() => openStudentAssessment(row.studentKey)}
            title="Open student assessment"
          >
            {row.studentKey}
          </button>
        </td>
        <td>{row.dateEnrolled}</td>
        <td>{row.studentName}</td>
        <td>{row.email}</td>
        <td>{row.phone}</td>
        <td>{row.locationAddress}</td>
        <td>
          <span className={`window-a-status window-a-status-${statusTone}`}>
            {accountStatus}
          </span>
        </td>
        <td>
          <button
            type="button"
            className="window-action-archive-btn"
            onClick={() => { void handleArchiveStudent(row.studentKey); }}
          >
            [ ARCHIVE ]
          </button>
        </td>
      </tr>
    );
  };

  const renderArchivedStudentRow = (row: ProgramStudentRow) => (
      <tr key={`archived-${row.studentKey}`}>
        <td><span className="window-a-key">{row.studentKey}</span></td>
        <td>{row.dateEnrolled}</td>
        <td>{row.studentName}</td>
        <td>{row.email}</td>
        <td>{row.phone}</td>
        <td>{row.locationAddress}</td>
        <td>
          <button
            type="button"
            className="window-action-assessment-btn"
            onClick={() => openStudentAssessment(row.studentKey)}
          >
            [ REVIEW / ASSESSMENT ]
          </button>
        </td>
        <td className="window-a-archive-reason-cell">
          <span className="window-a-archive-reason">{row.archiveReason || 'Archived token'}</span>
        </td>
      </tr>
    );

  const renderWindowA = () => (
    <div className="org-panel-card window-a-workspace">
      <h2 className="section-header window-a-heading">Mentology Classroom Students</h2>
      <p className="window-a-intro">
        Click a <strong>Student Key</strong> to open the 5-point assessment review, or use <strong>Archive</strong> to save the record to the vault spreadsheet.
      </p>
      <div className="window-a-table-wrap">
        <table className="window-a-table">
          <thead>
            <tr>
              <th>Student Key</th>
              <th>Date Enrolled</th>
              <th>Student Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Location Address</th>
              <th>Account Status</th>
              <th>Archive</th>
            </tr>
          </thead>
          <tbody>
            {registryLoading ? (
              <tr>
                <td colSpan={8} className="window-empty-row">
                  Loading student registry...
                </td>
              </tr>
            ) : programStudentRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="window-empty-row">
                  No active program students registered.
                </td>
              </tr>
            ) : (
              programStudentRows.map((row) => renderActiveStudentRow(row))
            )}
          </tbody>
        </table>
      </div>

      <div className="window-a-key-archive">
        <h3 className="window-a-key-archive-heading">Key Archive</h3>
        <p className="window-a-key-archive-intro">
          Assessed or archived student tokens. Use <strong>[ REVIEW / ASSESSMENT ]</strong> to open the 5-point review page.
        </p>
        <div className="window-a-table-wrap">
          <table className="window-a-table window-a-archive-table">
            <thead>
              <tr>
                <th>Student Key</th>
                <th>Date Enrolled</th>
                <th>Student Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Location Address</th>
                <th>Review / Assessment</th>
                <th>Archive Reason</th>
              </tr>
            </thead>
            <tbody>
              {registryLoading ? (
                <tr>
                  <td colSpan={8} className="window-empty-row">
                    Loading archived students...
                  </td>
                </tr>
              ) : archivedStudentRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="window-empty-row">
                    No archived student keys.
                  </td>
                </tr>
              ) : (
                archivedStudentRows.map((row) => renderArchivedStudentRow(row))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderWindowB = () => (
    <div className="org-panel-card window-b-workspace">
      <h2 className="section-header window-b-heading">Classroom Organisations</h2>
      <p className="window-b-intro">
        Active organisation tenants — use <strong>Archive</strong> when an organisation is no longer active.
        Records are written to <code>ML-02-Mentor Learning Vault\Archive\MLP_Organisation_Archive.csv</code>.
      </p>
      <div className="window-b-table-wrap">
        <table className="window-b-table">
          <thead>
            <tr>
              <th>Organisation Key</th>
              <th>Date Enrolled</th>
              <th>Organisation Name</th>
              <th>Contact Name</th>
              <th>Contact Info</th>
              <th>Subscription State</th>
              <th>Time Left</th>
              <th>Archive</th>
            </tr>
          </thead>
          <tbody>
            {registryLoading ? (
              <tr>
                <td colSpan={8} className="window-empty-row">
                  Loading organisation registry...
                </td>
              </tr>
            ) : organisationRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="window-empty-row">
                  No active classroom organisations registered.
                </td>
              </tr>
            ) : (
              organisationRows.map((row) => (
              <tr key={row.organisationKey}>
                <td><span className="window-b-key">{row.organisationKey}</span></td>
                <td>{row.dateEnrolled}</td>
                <td>{row.organisationName}</td>
                <td>{row.contactName}</td>
                <td>
                  <div className="window-b-contact-stack">
                    <span>{row.phone}</span>
                    <span>{row.email}</span>
                  </div>
                </td>
                <td>
                  <span className={`window-b-status window-b-status-${row.subscriptionState.replace(/\s+/g, '-').toLowerCase()}`}>
                    {row.subscriptionState}
                  </span>
                </td>
                <td>
                  <span className={`window-b-time-left ${row.subscriptionState === 'Lapsed' ? 'window-b-time-lapsed' : ''}`}>
                    {row.timeLeft}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="window-action-archive-btn"
                    onClick={() => handleArchiveOrganisation(row.organisationKey)}
                  >
                    Archive
                  </button>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderKnowledgeHub = () => (
    <div className="org-panel-card knowledge-hub-workspace">
      <h2 className="section-header knowledge-hub-heading">Learning Program Knowledge Hub</h2>
      <p className="knowledge-hub-intro">
        Dual-indexed research brain — Founder master specifications and ML-01 vault partition mapping.
      </p>

      <div className="knowledge-hub-tabs" role="tablist" aria-label="Knowledge Hub views">
        <button
          type="button"
          role="tab"
          aria-selected={knowledgeHubTab === 'masterSpecifications'}
          className={`knowledge-hub-tab ${knowledgeHubTab === 'masterSpecifications' ? 'active' : ''}`}
          onClick={() => setKnowledgeHubTab('masterSpecifications')}
        >
          MASTER SPECIFICATIONS
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={knowledgeHubTab === 'vaultPartitionMap'}
          className={`knowledge-hub-tab ${knowledgeHubTab === 'vaultPartitionMap' ? 'active' : ''}`}
          onClick={() => setKnowledgeHubTab('vaultPartitionMap')}
        >
          VAULT PARTITION MAP
        </button>
      </div>

      {knowledgeHubTab === 'masterSpecifications' ? (
        <div className="knowledge-hub-panel" role="tabpanel">
          <h3 className="knowledge-hub-panel-heading">Knowledge Engine Anchors</h3>
          <p className="knowledge-hub-panel-subtext">
            Five non-overlapping anchors binding the environment engine to Founder specifications and vault storage.
          </p>
          <table className="knowledge-hub-table knowledge-hub-unified-table">
            <thead>
              <tr>
                <th>ANCHOR / FILE</th>
                <th>GOVERNS &amp; CONTEXT MAP</th>
                <th>LOCAL / CLOUD PATH</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {KNOWLEDGE_ENGINE_ANCHORS.map((row) => (
                <tr key={row.id}>
                  <td className="knowledge-hub-anchor-cell">{row.anchorFile}</td>
                  <td>{row.governsContextMap}</td>
                  <td><code className="knowledge-hub-path-code">{row.localCloudPath}</code></td>
                  <td><span className="knowledge-hub-status">{row.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="knowledge-hub-panel" role="tabpanel">
          <h3 className="knowledge-hub-panel-heading">Structural Core Library Matrix</h3>
          <p className="knowledge-hub-panel-subtext">
            The environment interacts with raw file paths using an immutable storage schema. The system reads
            and references the Mentology data vault along this file structure:
          </p>
          <pre className="knowledge-hub-vault-tree">{STRUCTURAL_CORE_LIBRARY_MATRIX}</pre>
          <h3 className="knowledge-hub-panel-heading">Vault Partition Map</h3>
          <p className="knowledge-hub-panel-subtext">
            Ten structural folder partitions mapped via the M:\ network mount to the Andre-Knowledge vault tree.
          </p>
          <table className="knowledge-hub-table knowledge-hub-unified-table">
            <thead>
              <tr>
                <th>PARTITION</th>
                <th>ROLE</th>
                <th>PATH MAPPING (M:\ ROOT)</th>
              </tr>
            </thead>
            <tbody>
              {VAULT_PARTITIONS.map((row) => (
                <tr key={row.partition}>
                  <td className="knowledge-hub-anchor-cell">{row.partition}</td>
                  <td>{row.role}</td>
                  <td>
                    <div className="vault-partition-path-stack">
                      <span className="vault-partition-path-label">LOCAL PATH</span>
                      <code className="knowledge-hub-path-code">{row.localPath}</code>
                      <span className="vault-partition-path-label">CLOUD PATH</span>
                      <code className="knowledge-hub-path-code">{row.cloudPath}</code>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderLearningModules = () => {
    if (selectedModuleId) {
      return (
        <LearningModuleEditor
          moduleId={selectedModuleId}
          onBack={() => setSelectedModuleId(null)}
        />
      );
    }

    const matrixRows = learningModuleRows.length > 0
      ? learningModuleRows
      : LEARNING_MODULES.map((row) => ({
        moduleId: row.moduleId,
        title: row.title,
        stage: row.stage,
        updatedAt: null,
      }));

    return (
      <div className="org-panel-card learning-modules-workspace">
        <h2 className="section-header learning-modules-heading">Learning Modules</h2>
        <p className="learning-modules-intro">
          Active 10-module curriculum sourced from ML-01 vault (Module_01_10.docx on the Mentology Vault).
          Click a module row to open the Socratic Content Lab editor.
        </p>
        <table className="knowledge-hub-table learning-modules-matrix">
          <thead>
            <tr>
              <th>Module</th>
              <th>Title</th>
              <th>Journey Stage</th>
            </tr>
          </thead>
          <tbody>
            {learningModulesLoading ? (
              <tr>
                <td colSpan={3} className="window-empty-row">
                  Loading module matrix…
                </td>
              </tr>
            ) : (
              matrixRows.map((row) => (
                <tr
                  key={row.moduleId}
                  className="learning-module-row"
                  onClick={() => setSelectedModuleId(row.moduleId)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedModuleId(row.moduleId);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Edit Module ${row.moduleId}: ${row.title}`}
                >
                  <td>Module {row.moduleId}</td>
                  <td>{row.title}</td>
                  <td>{row.stage}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPanel = () => {
    if (activeSection === 'Students') {
      return renderWindowA();
    }
    if (activeSection === 'Organisations') {
      return renderWindowB();
    }
    if (activeSection === 'Learning Modules') {
      return renderLearningModules();
    }
    if (activeSection === 'Key Generator') {
      return renderKeyGenerator();
    }
    if (activeSection === 'System Data') {
      return renderSystemData();
    }
    if (activeSection === 'Maintenance Preview') {
      return <MasterAdminMaintenancePreview />;
    }
    return renderKnowledgeHub();
  };

  const handleSectionChange = (section: MasterAdminTab) => {
    setActiveSection(section);
  };

  const panelClassName =
    activeSection === 'Maintenance Preview'
      ? 'admin-panel-maintenance'
      : activeSection === 'Learning Modules'
        ? 'admin-panel-learning-modules'
        : '';

  return (
    <MasterAdminShell
      activeSection={activeSection}
      onSectionChange={handleSectionChange}
      panelClassName={panelClassName}
    >
      {renderPanel()}
      {renderProgramKeyEmailModal()}
    </MasterAdminShell>
  );
}
